import React, { useCallback, useContext, useEffect } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CreateLeadDto } from 'api/Leads/Types/CreateLeadDto'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import { CreateLeadForm } from './CreateLeadForm'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  countries: useAppSelector((state) => state.identity.countries),
  languages: useAppSelector((state) => state.clients.language),
  users: useAppSelector((state) => state.identity.administrationUsers),
  tickmillProducts: useAppSelector((state) => state.clients.tickmillProducts),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
  }
}

const CreateLeadPage = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const leadsApiClient = useApiClient(LeadsApiClient)
  const navigate = useNavigate()

  const { countries, tickmillCompanies, languages, users, tickmillProducts } = useMapStateToProps()
  const { fetchCountries } = useMapDispatchToProps()
  const dateFormatType = auth?.user.dateFormatType.name

  const callback = useCallback(() => {
    return leadsApiClient.getLeadSources({ importableOnly: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: leadSources = [] } = useFetchOne(callback)

  useEffect(() => {
    if (isEmpty(countries)) fetchCountries()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitCreateLead = async (values: CreateLeadDto) => {
    const response = await leadsApiClient.addLead(values)
    setToast(successToast('Lead created successfully.'))
    navigate(
      generatePath(links.recordsLeads.detailsUrl as string, {
        id: response.leadId,
        slug: 'details',
      })
    )
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        crumbs={[
          { title: 'Leads', link: links.recordsLeads.url },
          { title: 'Create Lead', link: links.createLead.url },
        ]}
      />
      <NavigationHeader />
      <CreateLeadForm
        leadSources={leadSources}
        tickmillCompanies={tickmillCompanies}
        countries={countries}
        languages={languages}
        users={users}
        tickmillProducts={tickmillProducts}
        dateFormatType={dateFormatType}
        onSubmit={handleSubmitCreateLead}
      />
    </React.Fragment>
  )
}

export default CreateLeadPage
